/** A record time axis's scale (DESIGN.md §5, "Time axis"): where an instant sits across
 *  the plot, 0–1. Pure.
 *
 *  - **Linear** — `axisX(ts, null, [from, to])`: one scale over a window (a person's
 *    schedule, an axis without a start time).
 *  - **Banded** — `axisX(ts, bounds, …)`: equal-width bands, each with its own linear
 *    scale, so a day on site reads as clearly as the weeks of waiting before it.
 *    `axisBounds` gives a booking's lifecycle bands (the spine's five stages).
 *
 *  `axisPct` turns a position into the CSS `left` / `width` the marks are placed with. */

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** What the lifecycle bands are built from: the axis window and the record's anchors — a
 *  booking timeline (`BookingTimeline`) as it comes. */
export interface AxisSpan {
  window: { from: string | null; to: string | null };
  anchors: {
    booking_ts?: string | null;
    created_ts?: string | null;
    start_ts: string | null;
    end_ts?: string | null;
  };
}

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/** The six boundaries t0…t5 of the five lifecycle bands, monotonic: Booked (the first
 *  day), Pre-check-in (the wait), Departure (the 24 h before the start), On-site (start →
 *  end; an hour without an end) and After (to the window's end or `extendTo`, whichever
 *  is later, and at least an hour). Null without a start after the booking: the axis is
 *  then one linear scale. */
export function axisBounds(t: AxisSpan, extendTo: number | null = null): number[] | null {
  const from = ms(t.window.from) ?? 0;
  const t0 = Math.min(...[from, ms(t.anchors.booking_ts), ms(t.anchors.created_ts)].filter((v): v is number => v !== null));
  const start = ms(t.anchors.start_ts);
  const winTo = Math.max(ms(t.window.to) ?? t0 + DAY, extendTo ?? 0);
  if (start === null || start <= t0) return null;
  const end = Math.max(ms(t.anchors.end_ts) ?? start + HOUR, start + 60_000);
  const t5 = Math.max(winTo, end + HOUR);
  const t2 = Math.max(t0, start - DAY);
  const t1 = Math.min(t0 + DAY, t2);
  return [t0, t1, t2, start, end, t5];
}

/** 0–1 across the plot for an instant, clamped to the plot: over the equal-width bands of
 *  `bounds`, or linear over `linear` when `bounds` is null. A zero-length band (or window)
 *  puts its points at its middle. */
export function axisX(ts: number, bounds: number[] | null, linear: [number, number]): number {
  if (!bounds) {
    const [a, b] = linear;
    return b > a ? Math.min(1, Math.max(0, (ts - a) / (b - a))) : 0.5;
  }
  const n = bounds.length - 1;
  for (let i = 0; i < n; i++) {
    const a = bounds[i]!;
    const b = bounds[i + 1]!;
    if (ts <= b || i === n - 1) {
      const frac = b > a ? Math.min(1, Math.max(0, (ts - a) / (b - a))) : 0.5;
      return (i + frac) / n;
    }
  }
  return 1;
}

/** A 0–1 position (or width) as a CSS percentage. */
export function axisPct(x: number): string {
  return `${(x * 100).toFixed(3)}%`;
}
